import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card.jsx';
import { TrendingUp, ArrowUp, ArrowDown, Minus, Target, AlertTriangle, Trophy, Shield, DollarSign, Heart, Leaf } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const CLASSIFICACAO = {
  lider: { label: 'Líder', color: 'emerald', icon: Trophy, desc: 'Acima de 80% dos indicadores de mercado' },
  competitivo: { label: 'Competitivo', color: 'blue', icon: TrendingUp, desc: 'Alinhado com as melhores práticas' },
  atencao: { label: 'Atenção', color: 'amber', icon: AlertTriangle, desc: 'Oportunidades de melhoria identificadas' },
  critico: { label: 'Crítico', color: 'red', icon: AlertTriangle, desc: 'Ações urgentes necessárias' },
  sem_dados: { label: 'Sem Dados', color: 'slate', icon: Target, desc: 'Responda pesquisas para calcular' },
};

const PILAR_CONFIG = {
  remuneracao: { label: 'Remuneração', icon: DollarSign, color: '#0f172a' },
  beneficios: { label: 'Benefícios', icon: Heart, color: '#2563eb' },
  esg: { label: 'ESG & Pessoas', icon: Leaf, color: '#059669' },
};

const CircularGauge = ({ score, size = 140, strokeWidth = 10 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = score !== null ? score / 100 : 0;
  const offset = circumference * (1 - pct);
  const color = score >= 80 ? '#059669' : score >= 60 ? '#2563eb' : score >= 40 ? '#d97706' : '#ef4444';

  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#e2e8f0" strokeWidth={strokeWidth} />
      {score !== null && (
        <circle
          cx={size / 2} cy={size / 2} r={radius} fill="none"
          stroke={color} strokeWidth={strokeWidth}
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-1000 ease-out"
        />
      )}
    </svg>
  );
};

const PilarBar = ({ pilar, config }) => {
  if (!pilar.has_data) {
    return (
      <div className="flex items-center gap-3 py-2 opacity-50">
        <config.icon className="w-4 h-4 text-slate-400" />
        <div className="flex-1">
          <div className="flex justify-between items-center mb-1">
            <span className="text-xs font-medium text-slate-500">{config.label} ({pilar.peso}%)</span>
            <span className="text-xs text-slate-400">Sem dados</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full" />
        </div>
      </div>
    );
  }

  const color = pilar.score >= 80 ? 'bg-emerald-500' : pilar.score >= 60 ? 'bg-blue-500' : pilar.score >= 40 ? 'bg-amber-500' : 'bg-red-500';

  return (
    <div className="flex items-center gap-3 py-2" data-testid={`pilar-${config.label.toLowerCase().replace(/\s+/g, '-')}`}>
      <config.icon className="w-4 h-4" style={{ color: config.color }} />
      <div className="flex-1">
        <div className="flex justify-between items-center mb-1">
          <span className="text-xs font-medium text-slate-700">{config.label} ({pilar.peso}%)</span>
          <span className="text-sm font-bold text-slate-900">{pilar.score}</span>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-700 ease-out ${color}`} style={{ width: `${pilar.score}%` }} />
        </div>
        <span className="text-[10px] text-slate-400">{pilar.n_itens} indicador(es) avaliado(s)</span>
      </div>
    </div>
  );
};

const CompetitivenessIndex = ({ variant = 'full', ano = 2024 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/competitiveness-index?ano=${ano}`, { headers: getHeaders() });
        setData(res.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, [ano]);

  if (loading) return <div className="animate-pulse h-24 bg-slate-100 rounded-lg" />;
  if (!data) return null;

  const cls = CLASSIFICACAO[data.classificacao] || CLASSIFICACAO.sem_dados;
  const ClsIcon = cls.icon;
  const score = data.index_score;
  const pilares = data.pilares || {};

  // ===== COMPACT variant (for benchmark dashboards) =====
  if (variant === 'compact') {
    if (score === null) return null;
    const bgColor = score >= 80 ? 'from-emerald-600 to-emerald-700' : score >= 60 ? 'from-blue-600 to-blue-700' : score >= 40 ? 'from-amber-500 to-amber-600' : 'from-red-500 to-red-600';
    return (
      <div className={`bg-gradient-to-r ${bgColor} rounded-lg p-3 text-white flex items-center gap-3`} data-testid="competitiveness-compact">
        <div className="relative">
          <CircularGauge score={score} size={48} strokeWidth={5} />
          <span className="absolute inset-0 flex items-center justify-center text-xs font-bold">{score}</span>
        </div>
        <div>
          <p className="text-[10px] opacity-70">Índice de Competitividade</p>
          <p className="text-sm font-bold">{cls.label}</p>
        </div>
      </div>
    );
  }

  // ===== FULL variant (for Dashboard & Minha Empresa) =====
  return (
    <Card className="overflow-hidden" data-testid="competitiveness-index">
      <div className={`${score === null ? 'bg-slate-100' : score >= 80 ? 'bg-gradient-to-r from-emerald-600 to-emerald-700' : score >= 60 ? 'bg-gradient-to-r from-blue-600 to-blue-700' : score >= 40 ? 'bg-gradient-to-r from-amber-500 to-amber-600' : 'bg-gradient-to-r from-red-500 to-red-600'} p-6`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-5">
            <div className="relative">
              <CircularGauge score={score} size={120} strokeWidth={8} />
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                {score !== null ? (
                  <>
                    <span className={`text-3xl font-bold ${score === null ? 'text-slate-400' : 'text-white'}`}>{score}</span>
                    <span className={`text-[10px] ${score === null ? 'text-slate-400' : 'text-white/60'}`}>/ 100</span>
                  </>
                ) : (
                  <span className="text-sm text-slate-400 font-medium">--</span>
                )}
              </div>
            </div>
            <div className={score === null ? 'text-slate-500' : 'text-white'}>
              <p className="text-xs opacity-60">Índice de Competitividade</p>
              <div className="flex items-center gap-2 mt-1">
                <ClsIcon className="w-5 h-5" />
                <p className="text-2xl font-bold">{cls.label}</p>
              </div>
              <p className="text-xs opacity-60 mt-1">{cls.desc}</p>
              <p className="text-[10px] opacity-40 mt-1">{data.pilares_ativos}/3 pilares com dados ({data.ano})</p>
            </div>
          </div>
        </div>
      </div>

      <CardContent className="p-5 space-y-1">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Composição por Pilar</p>
        {Object.entries(PILAR_CONFIG).map(([key, config]) => (
          <PilarBar key={key} pilar={pilares[key] || { has_data: false, peso: 0, n_itens: 0 }} config={config} />
        ))}
        {data.pilares_ativos === 0 && (
          <div className="text-center py-4 text-slate-400 text-sm">
            Responda pesquisas de Remuneração, Benefícios e/ou ESG para calcular seu índice.
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CompetitivenessIndex;
